/**
 * Image processing helpers — canvas-based re-encode (STORY-1103 Gate-7 D3+D4).
 *
 * Two-for-one: re-encoding via canvas discards EXIF (privacy default) AND
 * scales dimensions. PDFs and non-image types are pass-through. No external
 * dependency (browser-image-compression avoided intentionally).
 */

export interface ProcessImageOptions {
  maxDimension?: number;
  stripExif?: boolean;
  quality?: number;
}

export function scaleDimensions(
  srcWidth: number,
  srcHeight: number,
  maxDimension: number
): { width: number; height: number } {
  if (!maxDimension || (srcWidth <= maxDimension && srcHeight <= maxDimension)) {
    return { width: srcWidth, height: srcHeight };
  }
  const ratio = srcWidth / srcHeight;
  if (srcWidth >= srcHeight) {
    return { width: maxDimension, height: Math.round(maxDimension / ratio) };
  }
  return { width: Math.round(maxDimension * ratio), height: maxDimension };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = err => {
      URL.revokeObjectURL(url);
      reject(err instanceof Error ? err : new Error('Image load failed'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas encode failed'));
      },
      type,
      quality
    );
  });
}

export async function processImage(file: File, options: ProcessImageOptions = {}): Promise<File> {
  const { maxDimension, stripExif = true, quality = 0.9 } = options;
  if (!file.type.startsWith('image/')) return file;
  const needsReencode = stripExif || (maxDimension !== undefined && maxDimension > 0);
  if (!needsReencode) return file;

  try {
    const img = await loadImage(file);
    const { width, height } = scaleDimensions(
      img.naturalWidth || img.width,
      img.naturalHeight || img.height,
      maxDimension ?? Number.POSITIVE_INFINITY
    );
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, file.type, quality);
    return new File([blob], file.name, { type: file.type, lastModified: Date.now() });
  } catch {
    return file;
  }
}
